// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #2
// Lotto detail — semplificato per matchare il nuovo design mockup
// (Lotto Detail.html). Sezioni: Anagrafica · Asta & procedura · Sopralluoghi ·
// Matching · Documenti & SCD. Rail: countdown asta · numeri chiave · consulente ·
// cronologia mini.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { useProperty, usePatchProperty } from '../hooks/useProperty'
import { ZonaAglCell } from '../components/lotto/ZonaAglCell'
import { MutuabileBadge } from '../components/MutuabileBadge'
import { useCreateComment, useUsersList } from '../hooks/useComments'
import { useMe } from '../hooks/useAuth'
import { useActivity } from '../hooks/useActivity'
import { useAppointments } from '../hooks/useAppointments'
import {
  useDeleteDocument,
  useDocumentList,
  useFetchDocumentUrl,
  useUpdateDocumentKind,
  useUploadDocument,
} from '../hooks/useDocuments'
import { usePropertyMatches } from '../hooks/useMatches'
import { calendarDaysUntil } from '../lib/dateRelative'
import { toLocalDateInputValue } from '../lib/dateInput'
import { canWriteParticipation } from '../lib/permissions'
import { effectivePrice } from '../lib/price'
import { SendMessageModal } from '../components/SendMessageModal'
import { NewChatModal } from '../components/NewChatModal'
import { PropertyFormModal } from '../components/PropertyFormModal'
import { AppointmentFormModal } from '../components/AppointmentFormModal'
import { CardSkeleton } from '../components/Skeleton'
import { ScdFattibilitaTab } from '../components/property/ScdFattibilitaTab'
import { ScdRequestsList } from '../components/scd/ScdRequestsList'
import { useSavableField } from '../hooks/useSavableField'
import { useToast } from '../components/Toast'
import { AnalisiAiSection } from '../components/property/AnalisiAiSection'
import {
  fetchClients,
  fetchPropertyMapPois,
  geocodeProperty,
  setSopralluogoBlock,
  SOPRALLUOGO_BLOCK_LABEL,
  type ClientRow,
  type DocumentKind,
  type DocumentRow,
  type GeocodeResponse,
  type MapPoisResponse,
  type MatchForPropertyRow,
  type PropertyRow,
  type SopralluogoBlockReason,
} from '../lib/api'
import { useCreateParticipation } from '../hooks/useParticipation'

export const Route = createFileRoute('/lotti/$id')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: LottoDetailPage,
})

function shortCode(code: number | null | undefined): string {
  if (code === null || code === undefined) return ''
  return `L_${code.toString().padStart(5, '0')}`
}

// SCD Fattibilità section — Giulia workflow (scheda fattibilità + delegato +
// Lorenzo broker). Always visible on lotto detail.
function ScdFattibilitaPreview({ property }: { property: import('../lib/api').PropertyRow }) {
  return (
    <section className="section" id="scd-fattibilita">
      <div className="section-head">
        <div className="title">📋 SCD Fattibilità</div>
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        <ScdFattibilitaTab property={property} />
      </div>
    </section>
  )
}

function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v)
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('it-IT')
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function relativeTime(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 60) return `${min}m fa`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h fa`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}g fa`
  return d.toLocaleDateString('it-IT')
}

// ─── Messaggio marketing (template WhatsApp) ───────────────────────────────
// Estrae la "via" dall'indirizzo per la CTA: prende solo il primo segmento prima
// della virgola (scarta civico/CAP/città/nazione di un indirizzo geocodato),
// toglie prefisso (Via/Viale/…) e civico, restituisce maiuscolo.
// "Via Giorgio Bassani n. 43" → "GIORGIO BASSANI"
// "Viale degli Eroi di Rodi, 102, 00128 Roma RM, Italia" → "DEGLI EROI DI RODI"
function viaFromAddress(address: string | null | undefined): string {
  if (!address) return ''
  // Solo il primo segmento: scarta tutto dopo la prima virgola (civico, CAP,
  // città, provincia, nazione di un indirizzo geocodato Nominatim).
  let s = address.split(',')[0].trim()
  s = s.replace(
    /^(via|viale|v\.le|vicolo|piazza|p\.zza|p\.za|largo|corso|c\.so|strada|str\.|località|loc\.|borgo|salita|lungotevere|circonvallazione|traversa)\s+/i,
    '',
  )
  // togli civico finale rimasto nel primo segmento: " n. 43" / " n.43" / " 43" /
  // " 12/B" / " 12-14" / " snc"
  s = s.replace(/\s+n\.?\s*\d+.*$/i, '')
  s = s.replace(/\s+snc\b.*$/i, '')
  s = s.replace(/\s+\d+\s*[/-]?\s*\d*\s*[a-z]?\s*$/i, '')
  return s.trim().toUpperCase()
}

// Indirizzo per la riga 📍 del messaggio: via + civico + città. Scarta CAP,
// provincia e nazione di un indirizzo geocodato. Via+civico = primi 2 segmenti
// prima della virgola (il 2° solo se contiene un numero = civico); la città dal
// campo strutturato `comune`.
// "Viale degli Eroi di Rodi, 102, 00128 Roma RM, Italia" + comune "Roma"
//   → "Viale degli Eroi di Rodi, 102, Roma"
function indirizzoMarketing(
  address: string | null | undefined,
  comune: string | null | undefined,
): string {
  const parts = (address ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const via = parts[0] ?? ''
  const civico = parts[1] && /\d/.test(parts[1]) ? parts[1] : ''
  const viaCivico = [via, civico].filter(Boolean).join(', ')
  const citta = (comune ?? '').trim()
  // evita di ripetere la città se già presente nel pezzo via/civico
  const dupCitta =
    citta && viaCivico.toLowerCase().includes(citta.toLowerCase())
  return [viaCivico, dupCitta ? '' : citta].filter(Boolean).join(', ')
}

// Numero it-IT con separatore migliaia, 0 decimali. 737250 → "737.250".
function fmtMarketingNum(v: number | null | undefined): string {
  if (v == null) return ''
  return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(v)
}

// Data gg/mm/aaaa.
function fmtMarketingDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

// Assembla il draft del messaggio marketing dal lotto. Template SEMPRE completo:
// ogni riga è presente; dove manca il dato mette un placeholder `___` che il
// consulente riempie a mano nella textarea prima di inviare.
function buildMarketingMessage(p: PropertyRow): string {
  const PH = '___'
  const titolo = (p.zona_marketing ?? '').trim().toUpperCase()
  const indir = indirizzoMarketing(p.address, p.comune)
  const mq = p.superficie_mq != null ? fmtMarketingNum(p.superficie_mq) : PH
  // Prezzo: stessa logica unica della lista (offerta_minima>0 else base).
  const effPrezzo = effectivePrice(p.offerta_minima, p.prezzo_base)
  const prezzo =
    effPrezzo != null ? `€${fmtMarketingNum(effPrezzo)}` : PH
  const valore =
    p.valore_mercato != null ? `€${fmtMarketingNum(p.valore_mercato)}` : PH
  const dataStr = fmtMarketingDate(p.data_asta) || PH

  const lines: string[] = [
    `*${titolo || PH}*`,
    `\u{1F4CD} *${indir || PH}*`,
    `\u{1F4CF} *Mq ${mq}*`,
    `\u{1F3F7}\u{FE0F} Prezzo: *${prezzo}*`,
    `\u{1F4B0} Valore: *${valore}*`,
    `\u{1F4C5} Asta: *${dataStr}*`,
  ]
  let body = lines.join('\n')

  // Descrizione (sempre una riga; placeholder se l'AI non l'ha generata).
  const desc = (p.descrizione_marketing ?? '').trim()
  body += `\n\n${desc || PH}`

  // RISPARMIO: calcolato se valore+prezzo presenti e coerenti, altrimenti ___%.
  let risparmio = `${PH}%`
  if (
    p.valore_mercato != null &&
    effPrezzo != null &&
    p.valore_mercato > 0 &&
    effPrezzo <= p.valore_mercato
  ) {
    risparmio = `${Math.round(
      ((p.valore_mercato - effPrezzo) / p.valore_mercato) * 100,
    )}%`
  }
  body += `\n\n\u{1F680} *RISPARMIO ${risparmio}*`

  // CTA: via dall'indirizzo, placeholder se non estraibile.
  const via = viaFromAddress(p.address) || PH
  body += `\n\nRispondi *SI ${via}* per prenotare il sopralluogo.`

  return body
}

function LottoDetailPage() {
  const matchRoute = useMatchRoute()
  const onChild = matchRoute({ to: '/lotti/$id/match' })
  if (onChild) return <Outlet />
  return <LottoDetail />
}

function LottoDetail() {
  const { id } = Route.useParams()
  const { data: me } = useMe()
  const { data, isLoading, isError } = useProperty(id)
  const activityQuery = useActivity({ property_id: id, page_size: 100 })
  const appointmentsQuery = useAppointments({
    property_id: id,
    page_size: 20,
    sort: '-scheduled_at',
  })
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [editOpen, setEditOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [uploadDocsNonce, setUploadDocsNonce] = useState(0)
  const canEdit = canWriteParticipation(me)

  const code = data ? shortCode(data.short_code) : ''
  const rawTitle =
    data?.title ?? data?.address ?? data?.comune ?? 'Lotto'
  const displayTitle =
    rawTitle.length > 80 ? rawTitle.slice(0, 77) + '…' : rawTitle

  const activityItems = activityQuery.data?.items ?? []
  const sopralluoghi = appointmentsQuery.data?.items ?? []
  const ctSopralluoghi = sopralluoghi.length
  const ctMatching = activityItems.filter((a) => a.kind === 'match').length
  const ctDocs = activityItems.filter((a) => a.kind === 'allegato_upload').length

  // Last update meta — show most recent activity author/time (fallback to
  // property.created_at when timeline empty).
  const lastEvent = activityItems[0]
  const lastUpdateAt = lastEvent?.occurred_at ?? data?.created_at ?? null
  const lastUpdateBy =
    (lastEvent && 'actor_name' in lastEvent && (lastEvent as { actor_name?: string | null }).actor_name) ||
    data?.consultant_name ||
    null

  // Scrive direttamente la risposta del PATCH in cache (più veloce di un
  // blind-invalidate — il dato è già fresco) invece di re-invalidare come
  // fanno gli altri editor della pagina (StageSwitcher/MoreMenu/
  // MarketingMessageCard) — approccio diverso, non lo stesso meccanismo.
  const handleUpdate = (updated: PropertyRow) => {
    qc.setQueryData(['property', id], updated)
    void qc.invalidateQueries({ queryKey: ['properties'] })
  }

  return (
    <>
      {/* CRUMB row */}
      <div className="crumb-row">
        <div className="breadcrumb">
          <span>Operativo</span>
          <span className="sep">/</span>
          <a
            href="/lotti"
            onClick={(e) => { e.preventDefault(); window.history.back() }}
          >
            Lotti
          </a>
          <span className="sep">/</span>
          <span className="here">
            {code} · {displayTitle}
          </span>
        </div>
      </div>

      {isLoading && <CardSkeleton />}
      {isError && (
        <div
          className="muted"
          style={{ padding: 'var(--sp-4)', fontStyle: 'italic' }}
        >
          Lotto non trovato o errore di caricamento.
        </div>
      )}

      {data && (
        <>
          {/* HERO — no gallery, single summary block */}
          <div className="hero detail-hero">
            <div className="hero-main">
              <div
                className="title-row"
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 'var(--sp-2)',
                  flexWrap: 'wrap',
                }}
              >
                <span className="tag code lot-code">{code}</span>
                <h1
                  className="lot-title"
                  style={{
                    margin: 0,
                    fontSize: 'var(--fs-18)',
                    fontWeight: 'var(--fw-semibold)',
                  }}
                >
                  {displayTitle}
                </h1>
              </div>

              <div
                className="lot-sub muted"
                style={{
                  display: 'flex',
                  gap: 'var(--sp-2)',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  fontSize: 'var(--fs-12)',
                }}
              >
                <span>
                  {data.address}
                  {data.comune && ` · ${data.comune}`}
                  {data.provincia && ` (${data.provincia})`}
                  {data.cap && ` · ${data.cap}`}
                </span>
                {!data.data_asta && (
                  <span
                    className="chip warn"
                    title="Data asta non ancora definita dal tribunale. Verifica portale o contatta custode."
                    style={{
                      fontSize: 'var(--fs-11)',
                      padding: '2px 8px',
                      background: 'oklch(96% 0.05 80)',
                      color: 'oklch(45% 0.15 60)',
                      border: '1px solid oklch(85% 0.08 80)',
                      borderRadius: 'var(--r-pill)',
                      fontWeight: 'var(--fw-semibold)',
                    }}
                  >
                    ⚠ Data asta da definire
                  </span>
                )}
                {data.occupazione && data.occupazione !== 'sconosciuto' && (
                  <span
                    className={`chip ${data.occupazione === 'libero' ? 'ok' : 'outline'}`}
                  >
                    <span
                      className="dot"
                      style={{
                        background:
                          data.occupazione === 'libero'
                            ? 'var(--ok)'
                            : 'var(--risk)',
                      }}
                    ></span>
                    {data.occupazione.replace(/_/g, ' ')}
                  </span>
                )}
                {data.modalita_vendita && (
                  <span className={`tag tab-${data.modalita_vendita.toLowerCase()}`}>
                    {data.modalita_vendita.toUpperCase()}
                  </span>
                )}
                {data.is_residenziale === false && (
                  <span
                    className="chip outline"
                    title={`Non residenziale${data.tipologia ? ` (${data.tipologia})` : ''} — box, posto auto, cantina, negozio o simili`}
                  >
                    📦 Non residenziale
                  </span>
                )}
                <MutuabileBadge lorenzoInviato={data.lorenzo_inviato} mutuabile={data.mutuabile} />
                {data.catastale?.categoria && (
                  <span>
                    Cat. <b>{data.catastale.categoria}</b>
                  </span>
                )}
                {data.classe_energetica && (
                  <span>
                    APE <b>{data.classe_energetica}</b>
                  </span>
                )}
              </div>

            </div>

            <div className="hero-actions">
              {data.stage && (
                <StageSwitcher
                  propertyId={id}
                  currentStage={data.stage}
                  canEdit={canEdit}
                />
              )}
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setEditOpen(true)}
                  title="Modifica anagrafica lotto"
                >
                  ✎ Modifica
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setMessageOpen(true)}
                >
                  📋 Template MSG
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setChatOpen(true)}
                  title="Apri una chat di team su questo lotto"
                >
                  🗨️ Chat team
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setUploadDocsNonce((n) => n + 1)}
                >
                  📤 Documenti
                </button>
              )}
              <button
                type="button"
                className="btn accent"
                onClick={() =>
                  void navigate({
                    to: '/lotti/$id/match',
                    params: { id },
                    search: {} as never,
                  })
                }
              >
                🎯 Match cliente
              </button>
              <MoreMenu property={data} canEdit={canEdit} />
            </div>
          </div>

          <LorenzoMutuabileCard property={data} onUpdate={handleUpdate} canEdit={canEdit} />

          {/* ANCHOR NAV */}
          <nav className="anchor-nav">
            <a href="#anagrafica" className="on">
              Anagrafica
            </a>
            <a href="#asta">
              Asta &amp; procedura
              {data.data_asta && (
                <span className="ct">{fmtDate(data.data_asta)}</span>
              )}
            </a>
            <a href="#sopralluoghi">
              Sopralluoghi
              {ctSopralluoghi > 0 && <span className="ct">{ctSopralluoghi}</span>}
            </a>
            <a href="#matching">
              Matching
              {ctMatching > 0 && <span className="ct">{ctMatching}</span>}
            </a>
            <a href="#scd-fattibilita">SCD Fattibilità</a>
            <a href="#scd-requests">Richieste SCD</a>
            <a href="#documenti">
              Documenti &amp; SCD
              {ctDocs > 0 && <span className="ct">{ctDocs}</span>}
            </a>
            <div className="nav-end">
              {lastUpdateAt && (
                <span
                  className="muted"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 'var(--fs-11)',
                  }}
                  title={fmtDateTime(lastUpdateAt)}
                >
                  <span aria-hidden>🕒</span>
                  Aggiornato {relativeTime(lastUpdateAt)}
                  {lastUpdateBy && (
                    <>
                      {' · da '}
                      <strong style={{ color: 'var(--ink-2)' }}>
                        {lastUpdateBy}
                      </strong>
                    </>
                  )}
                </span>
              )}
            </div>
          </nav>

          {/* MAIN GRID */}
          <div
            className="detail-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 320px',
              gap: 'var(--sp-3)',
              alignItems: 'start',
            }}
          >
            <div
              className="detail-main"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-3)',
              }}
            >
              <AnagraficaSection p={data} canEdit={canEdit} />
              <AstaSection p={data} />
              <ScdFattibilitaPreview property={data} />
              <ScdRequestsList
                propertyId={id}
                property={{
                  id,
                  short_code: data.short_code,
                  rge: data.rge,
                  lorenzo_inviato: data.lorenzo_inviato,
                  lorenzo_esito: data.lorenzo_esito,
                  mutuabile: data.mutuabile,
                }}
                title="Richieste SCD per questo lotto"
              />
              <SopralluoghiSection
                items={sopralluoghi}
                propertyId={id}
                propertyLabel={`${data.comune ?? '—'} – ${data.address ?? '—'}`}
                block={data.sopralluogo_block ?? null}
              />
              <MatchingSection propertyId={id} />
              <AnalisiAiSection propertyId={id} />
              <DocumentiSection
                propertyId={id}
                canEdit={canEdit}
                openPickerNonce={uploadDocsNonce}
              />
            </div>

            <div
              className="detail-rail"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-3)',
              }}
            >
              <CountdownCard
                dataAsta={data.data_asta}
                propertyId={id}
                tribunale={data.tribunale}
              />
              <NumeriChiaveCard p={data} />
              <ConsulenteCard
                name={data.consultant_name}
              />
              <CronologiaMiniCard
                items={activityItems}
                isLoading={activityQuery.isLoading}
              />
            </div>
          </div>
        </>
      )}

      {data && (
        <PropertyFormModal
          open={editOpen}
          property={data}
          onClose={() => setEditOpen(false)}
        />
      )}

      {data && messageOpen && (
        <SendMessageModal
          open={messageOpen}
          onClose={() => setMessageOpen(false)}
          context={{
            property_id: id,
            property_code: code,
            property_address: data.address ?? null,
            property_comune: data.comune ?? null,
          }}
        />
      )}

      {data && (
        <NewChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          contextType="property"
          propertyId={id}
          contextLabel={`${code} · ${data.address ?? ''}`.trim()}
        />
      )}
    </>
  )
}

function LorenzoMutuabileCard({
  property,
  onUpdate,
  canEdit,
}: {
  property: PropertyRow
  onUpdate: (p: PropertyRow) => void
  canEdit: boolean
}) {
  const toast = useToast()
  const { value, setValue, dirty, saving, doSave } = useSavableField<
    { inviato: boolean; esito: string; mutuabile: boolean | null },
    PropertyRow
  >(
    {
      inviato: property.lorenzo_inviato ?? false,
      esito: property.lorenzo_esito ?? '',
      mutuabile: (property.mutuabile ?? null) as boolean | null,
    },
    async (v) => {
      const { patchProperty } = await import('../lib/api')
      return patchProperty(property.id, {
        lorenzo_inviato: v.inviato,
        lorenzo_esito: v.esito.trim() || null,
        mutuabile: v.mutuabile,
      })
    },
    {
      onSaved: (u) => {
        onUpdate(u)
        toast.success(
          value.mutuabile === true && property.mutuabile !== true
            ? 'Salvato — notificati tutti i consulenti'
            : 'Salvato',
        )
      },
      onError: () => toast.error('Errore salvataggio'),
    },
  )

  // "Incerto" ha valore null — indistinguibile da "pill mai cliccato" nel
  // solo dato value.mutuabile. Serve un flag esplicito per la scelta fatta
  // in questa sessione, PIÙ un bypass per un lotto già risolto prima del
  // mount (altrimenti ogni revisita di un lotto già "incerto" ribloccherebbe
  // il salvataggio finché non si riclicca un pill — stesso bug, solo differito).
  const [mutuabileTouched, setMutuabileTouched] = useState(false)
  const alreadyResolved = property.lorenzo_inviato === true
  const needsChoice = value.inviato && value.mutuabile === null && !mutuabileTouched && !alreadyResolved

  if (!canEdit) {
    return null
  }

  return (
    <div
      className={`lorenzo-card${dirty ? ' dirty' : ''}`}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-3)',
        marginBottom: 'var(--sp-3)',
      }}
    >
      <label className="lorenzo-toggle-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <button
          type="button"
          role="switch"
          aria-checked={value.inviato}
          className={`toggle-switch${value.inviato ? ' on' : ''}`}
          onClick={() => setValue((v) => ({ ...v, inviato: !v.inviato }))}
        />
        Inviato a Lorenzo (broker)
      </label>

      {value.inviato && (
        <>
          <div style={{ marginTop: 'var(--sp-2)' }}>
            <span className="k" style={{ display: 'block', marginBottom: 4 }}>Mutuabile</span>
            <div className="mutuabile-seg">
              {(
                [
                  { key: true, label: '✓ Sì', cls: 'ok' },
                  { key: false, label: '✗ No', cls: 'risk' },
                  { key: null, label: 'Incerto', cls: 'outline' },
                ] as const
              ).map((o) => (
                <button
                  key={String(o.key)}
                  type="button"
                  className={`mutuabile-pill${value.mutuabile === o.key ? ` sel ${o.cls}` : ''}`}
                  onClick={() => {
                    setValue((v) => ({ ...v, mutuabile: o.key }))
                    setMutuabileTouched(true)
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {needsChoice && (
              <span className="muted" style={{ fontSize: 'var(--fs-11)', display: 'block', marginTop: 4, color: 'var(--risk-ink)' }}>
                Seleziona un'opzione prima di salvare.
              </span>
            )}
            {value.mutuabile === true && property.mutuabile !== true && (
              <span className="muted" style={{ fontSize: 'var(--fs-11)', display: 'block', marginTop: 4 }}>
                📢 Al salvataggio notifica TUTTI i consulenti attivi.
              </span>
            )}
          </div>

          <div className="form-field" style={{ marginTop: 'var(--sp-2)' }}>
            <label>Note (opzionale)</label>
            <input
              value={value.esito}
              onChange={(e) => setValue((v) => ({ ...v, esito: e.target.value }))}
              placeholder="es. fattibile con perizia aggiornata"
            />
          </div>
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
        <button
          type="button"
          className={`btn sm${dirty ? ' dirty' : ''}`}
          disabled={saving || needsChoice}
          onClick={() => void doSave()}
        >
          {saving ? '…' : 'Salva'}
        </button>
        {dirty && <span className="dirty-badge">da salvare</span>}
      </div>
    </div>
  )
}

function MoreMenu({
  property,
  canEdit,
}: {
  property: PropertyRow
  canEdit: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const patchMut = usePatchProperty(property.id)
  const createComment = useCreateComment()
  const [issueOpen, setIssueOpen] = useState(false)
  const [issueText, setIssueText] = useState('')

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  function copyLink() {
    if (!property.source_url) {
      window.alert('Nessun link annuncio per questo lotto.')
      return
    }
    void navigator.clipboard
      .writeText(property.source_url)
      .then(() => {
        setOpen(false)
      })
      .catch(() => window.alert('Impossibile copiare link.'))
  }

  function exportPdf() {
    setOpen(false)
    window.print()
  }

  async function archiveLot() {
    setOpen(false)
    if (
      !window.confirm(
        'Archiviare questo lotto? Verrà spostato in "scartata" e sparirà dalle viste attive.',
      )
    ) {
      return
    }
    try {
      await patchMut.mutateAsync({ stage: 'scartata' })
      void qc.invalidateQueries({ queryKey: ['properties'] })
      void navigate({ to: '/lotti', search: {} as never })
    } catch (err) {
      window.alert(`Archiviazione fallita: ${(err as Error).message}`)
    }
  }

  function reportIssue() {
    setOpen(false)
    setIssueOpen(true)
  }

  async function submitIssue() {
    if (!issueText.trim()) return
    try {
      await createComment.mutateAsync({
        entity_type: 'property',
        entity_id: property.id,
        body: `🚫 Problema segnalato: ${issueText.trim()}`,
      })
      setIssueText('')
      setIssueOpen(false)
      void qc.invalidateQueries({ queryKey: ['activity'] })
    } catch (err) {
      window.alert(`Invio segnalazione fallito: ${(err as Error).message}`)
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen((v) => !v)}
        title="Altre azioni"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        ⋯
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            zIndex: 50,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            minWidth: 220,
            padding: 4,
            fontSize: 'var(--fs-12)',
          }}
        >
          <MoreItem icon="🔗" label="Copia link annuncio" onClick={copyLink} />
          <MoreItem icon="📤" label="Export PDF dettaglio" onClick={exportPdf} />
          {canEdit && (
            <MoreItem
              icon="🗑"
              label="Archivia lotto"
              onClick={archiveLot}
              danger
            />
          )}
          <MoreItem icon="🚫" label="Segnala problema" onClick={reportIssue} />
        </div>
      )}
      {issueOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIssueOpen(false)
          }}
        >
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <button
              type="button"
              className="modal-close"
              aria-label="Chiudi"
              onClick={() => setIssueOpen(false)}
            >
              ×
            </button>
            <div className="modal-body">
              <h2 style={{ marginTop: 0, fontSize: 18 }}>Segnala problema</h2>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                La segnalazione viene salvata come nota nello storico del lotto.
              </p>
              <textarea
                value={issueText}
                onChange={(e) => setIssueText(e.target.value)}
                rows={5}
                placeholder="Descrivi il problema (es. annuncio scaduto, dati errati, foto mancanti...)"
                style={{ width: '100%' }}
              />
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'flex-end',
                  marginTop: 12,
                }}
              >
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setIssueOpen(false)}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="btn accent"
                  disabled={!issueText.trim() || createComment.isPending}
                  onClick={() => void submitIssue()}
                >
                  {createComment.isPending ? 'Invio…' : 'Segnala'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MoreItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: string
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 10px',
        borderRadius: 'var(--r-sm)',
        background: 'transparent',
        border: 'none',
        textAlign: 'left',
        color: danger ? 'var(--risk-ink, #b91c1c)' : 'var(--ink-1)',
        cursor: 'pointer',
        fontSize: 'var(--fs-12)',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-2)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <span aria-hidden>{icon}</span>
      {label}
    </button>
  )
}

// Stage workflow lotto: 13 fasi pipeline. Label IT user-friendly.
const STAGE_OPTIONS: Array<{ value: string; label: string; group: string }> = [
  { value: 'mappatura', label: 'Mappatura', group: 'iniziale' },
  { value: 'bacheca', label: 'Bacheca', group: 'iniziale' },
  { value: 'scd_richiesta', label: 'SCD richiesta', group: 'scd' },
  { value: 'scd_pronta_fissare', label: 'SCD pronta', group: 'scd' },
  { value: 'scd_fissate', label: 'SCD fissate', group: 'scd' },
  { value: 'attesa_esito', label: 'Attesa esito', group: 'scd' },
  { value: 'esito_pos_checklist', label: 'Esito pos. → checklist', group: 'asta' },
  { value: 'domanda_asta', label: 'Domanda asta', group: 'asta' },
  { value: 'data_vendita', label: 'Data vendita', group: 'asta' },
  { value: 'aggiudicata', label: 'Aggiudicata', group: 'esito' },
  { value: 'non_aggiudicata', label: 'Non aggiudicata', group: 'esito' },
  { value: 'esito_neg_scd', label: 'Esito neg. SCD', group: 'esito' },
  { value: 'scartata', label: 'Scartata', group: 'esito' },
]

const STAGE_LABEL: Record<string, string> = Object.fromEntries(
  STAGE_OPTIONS.map((s) => [s.value, s.label]),
)

function StageSwitcher({
  propertyId,
  currentStage,
  canEdit,
}: {
  propertyId: string
  currentStage: string
  canEdit: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const patchMut = usePatchProperty(propertyId)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  function onSelect(stage: string) {
    if (stage === currentStage) {
      setOpen(false)
      return
    }
    patchMut.mutate(
      { stage: stage as never },
      { onSuccess: () => setOpen(false) },
    )
  }

  const label = STAGE_LABEL[currentStage] ?? currentStage.replace(/_/g, ' ')
  const stageClass = currentStage.replace(/_/g, '-')

  // Read-only: badge only.
  if (!canEdit) {
    return (
      <span className={`stage ${stageClass} lg`} title="Stage pipeline">
        {label}
      </span>
    )
  }

  // Group options for menu sections.
  const groups: Record<string, typeof STAGE_OPTIONS> = {}
  for (const opt of STAGE_OPTIONS) {
    if (!groups[opt.group]) groups[opt.group] = []
    groups[opt.group].push(opt)
  }
  const groupLabels: Record<string, string> = {
    iniziale: 'Iniziale',
    scd: 'SCD',
    asta: 'Asta',
    esito: 'Esito',
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={patchMut.isPending}
        className={`stage ${stageClass} lg`}
        title="Sposta stage"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          cursor: patchMut.isPending ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 0,
        }}
      >
        {patchMut.isPending ? '⏳' : ''} {label}
        <span aria-hidden style={{ fontSize: 10, opacity: 0.7 }}>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 50,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            minWidth: 240,
            padding: 6,
            fontSize: 'var(--fs-12)',
            maxHeight: 420,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: '4px 10px 6px',
              fontSize: 'var(--fs-10)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-caps)',
              color: 'var(--ink-3)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            Sposta a fase
          </div>
          {Object.entries(groups).map(([gKey, opts]) => (
            <div key={gKey}>
              <div
                style={{
                  padding: '6px 10px 2px',
                  fontSize: 9,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  color: 'var(--ink-4, #9ca3af)',
                  fontWeight: 'var(--fw-medium)',
                }}
              >
                {groupLabels[gKey] ?? gKey}
              </div>
              {opts.map((opt) => {
                const isCurrent = opt.value === currentStage
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitem"
                    onClick={() => onSelect(opt.value)}
                    disabled={patchMut.isPending || isCurrent}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: 'var(--r-sm)',
                      background: isCurrent ? 'var(--accent-soft)' : 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: isCurrent ? 'default' : 'pointer',
                      color: isCurrent ? 'var(--accent-ink)' : 'var(--ink-1)',
                      fontSize: 'var(--fs-12)',
                      fontWeight: isCurrent
                        ? 'var(--fw-semibold)'
                        : 'var(--fw-regular)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrent) {
                        ;(e.currentTarget as HTMLButtonElement).style.background =
                          'var(--bg-2)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrent) {
                        ;(e.currentTarget as HTMLButtonElement).style.background =
                          'transparent'
                      }
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        textAlign: 'center',
                        color: 'var(--accent)',
                      }}
                    >
                      {isCurrent ? '●' : ''}
                    </span>
                    {opt.label}
                  </button>
                )
              })}
            </div>
          ))}
          {patchMut.isError && (
            <div
              className="error"
              style={{
                margin: '6px 6px 0',
                fontSize: 'var(--fs-11)',
              }}
            >
              {patchMut.error?.message ?? 'Errore aggiornamento stage'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AnagraficaSection({
  p,
  canEdit,
}: {
  p: PropertyRow
  canEdit: boolean
}) {
  const hasCoords = p.lat != null && p.lng != null
  const poisQ = useQuery<MapPoisResponse>({
    queryKey: ['property', p.id, 'map-pois'],
    queryFn: () => fetchPropertyMapPois(p.id),
    enabled: hasCoords,
    staleTime: 1000 * 60 * 60, // 1h
    retry: false,
  })
  const pois = poisQ.data
  const coordStr =
    p.lat != null && p.lng != null
      ? `${Number(p.lat).toFixed(4)}° N, ${Number(p.lng).toFixed(4)}° E`
      : '—'

  return (
    <>
    <section className="section" id="anagrafica">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>📍</span>
          Anagrafica immobile
        </div>
      </div>
      <div
        className="section-body"
        style={{
          padding: 'var(--sp-3)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
          gap: 'var(--sp-3)',
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <KVTable
            rows={[
              ['Indirizzo', p.address ?? '—'],
              [
                'Comune',
                [p.comune, p.provincia && `(${p.provincia})`, p.cap]
                  .filter(Boolean)
                  .join(' · ') || '—',
              ],
              ['Coordinate', coordStr],
            ]}
          />
          <ZonaAglCell p={p} canEdit={canEdit} />
        </div>
        {hasCoords ? (
          <MapPoisMini
            lat={Number(p.lat)}
            lng={Number(p.lng)}
            data={pois}
            isLoading={poisQ.isLoading}
          />
        ) : (
          <MapPlaceholder
            propertyId={p.id}
            address={p.address}
            comune={p.comune}
            cap={p.cap}
          />
        )}
      </div>
    </section>
    <MarketingMessageCard p={p} canEdit={canEdit} />
    </>
  )
}

function MarketingMessageCard({
  p,
  canEdit,
}: {
  p: PropertyRow
  canEdit: boolean
}) {
  const patchMut = usePatchProperty(p.id)
  // Draft ricostruito dai campi del lotto. Stringa usata come dep dell'effetto
  // (confronto per valore) → la textarea si rinfresca quando l'analisi AI riempie
  // nuovi campi (zona/descrizione/mq/valore), anche senza cambio di id/messaggio.
  const draft = buildMarketingMessage(p)
  const [text, setText] = useState<string>(p.messaggio_marketing ?? draft)
  const [copied, setCopied] = useState(false)
  const [saved, setSaved] = useState(false)

  // Riallinea quando cambia il lotto, il messaggio salvato, o il draft (nuovi
  // dati dall'analisi AI). Se l'utente ha salvato (messaggio_marketing), mostra
  // quello. Edit non salvati: NON clobberati su re-render generici (draft
  // invariato), SÌ dopo una nuova analisi (draft cambia — voluto).
  useEffect(() => {
    setText(p.messaggio_marketing ?? draft)
  }, [p.id, p.messaggio_marketing, draft])

  function copy() {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  function rigenera() {
    setText(buildMarketingMessage(p))
    setSaved(false)
  }
  function salva() {
    patchMut.mutate(
      { messaggio_marketing: text },
      {
        onSuccess: () => {
          setSaved(true)
          setTimeout(() => setSaved(false), 1500)
        },
      },
    )
  }

  return (
    <section className="section" id="messaggio-marketing">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>
            {'\u{1F4E3}'}
          </span>
          Messaggio marketing
        </div>
      </div>
      <div
        className="section-body"
        style={{
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
        }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          readOnly={!canEdit}
          rows={12}
          style={{
            width: '100%',
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: 'var(--fs-12)',
            lineHeight: 1.5,
            padding: 'var(--sp-2)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            background: 'var(--bg-2)',
            color: 'var(--ink-1)',
            resize: 'vertical',
          }}
        />
        <div
          style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}
        >
          <button type="button" className="btn sm" onClick={copy}>
            {copied ? '✓ Copiato' : '\u{1F4CB} Copia'}
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                className="btn sm primary"
                onClick={salva}
                disabled={patchMut.isPending}
              >
                {patchMut.isPending
                  ? 'Salvataggio…'
                  : saved
                    ? '✓ Salvato'
                    : '\u{1F4BE} Salva'}
              </button>
              <button type="button" className="btn sm" onClick={rigenera}>
                {'↻ Rigenera'}
              </button>
            </>
          )}
          {patchMut.isError && (
            <span style={{ color: 'var(--risk-ink)', fontSize: 'var(--fs-11)' }}>
              Errore salvataggio
            </span>
          )}
        </div>
      </div>
    </section>
  )
}

function MapPlaceholder({
  propertyId,
  address,
  comune,
  cap,
}: {
  propertyId: string
  address: string | null
  comune: string | null
  cap: string | null
}) {
  const qc = useQueryClient()
  const [lastResult, setLastResult] = useState<GeocodeResponse | null>(null)
  const geocodeMut = useMutation({
    mutationFn: () => geocodeProperty(propertyId),
    onSuccess: (data) => {
      setLastResult(data)
      // If coords were resolved, invalidate property query so the parent
      // re-renders with the new lat/lng and switches to MapPoisMini.
      if (data.lat != null && data.lng != null) {
        void qc.invalidateQueries({ queryKey: ['property', propertyId] })
        void qc.invalidateQueries({
          queryKey: ['property', propertyId, 'map-pois'],
        })
      }
    },
  })

  const q = encodeURIComponent(
    [address, comune, cap].filter(Boolean).join(', '),
  )
  const osmSearch = `https://www.openstreetmap.org/search?query=${q}`
  const pending = geocodeMut.isPending
  const failed = lastResult?.quality === 'none' || geocodeMut.isError

  return (
    <div
      style={{
        border: '1px dashed var(--line)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-3)',
        background: 'var(--bg-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        alignItems: 'flex-start',
        fontSize: 'var(--fs-11)',
      }}
    >
      <span aria-hidden style={{ fontSize: 24 }}>🗺</span>
      <div>
        <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-12)' }}>
          Coordinate non disponibili
        </div>
        <div className="muted" style={{ marginTop: 2 }}>
          {failed
            ? 'Nominatim non ha trovato match per questo indirizzo. Cerca manualmente:'
            : 'Sorgente non ha fornito lat/lng. Tenta geocoding automatico via OpenStreetMap:'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        {!failed && (
          <button
            type="button"
            className="btn sm accent"
            onClick={() => geocodeMut.mutate()}
            disabled={pending}
          >
            {pending ? '⏳ Georeferenzio…' : '📍 Georeferenzia ora'}
          </button>
        )}
        {q && (
          <a
            href={osmSearch}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--accent-ink)',
              fontSize: 'var(--fs-11)',
              alignSelf: 'center',
            }}
          >
            Cerca su OpenStreetMap ↗
          </a>
        )}
      </div>
      {geocodeMut.isError && (
        <div className="error" style={{ fontSize: 'var(--fs-10)' }}>
          Errore geocoding. Riprova più tardi.
        </div>
      )}
    </div>
  )
}

function MapPoisMini({
  lat,
  lng,
  data,
  isLoading,
}: {
  lat: number
  lng: number
  data: MapPoisResponse | undefined
  isLoading: boolean
}) {
  // Open Street Map static URL is rate-limited; use OSM tile via embed link.
  // For a real map fallback we just show metadata + link out to OSM.
  const osmUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`
  const metro = data?.isocrone?.metro
  const scuole = data?.isocrone?.scuole_1km ?? 0
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
        background: 'var(--bg-2)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'relative',
          aspectRatio: '16 / 10',
          background:
            'linear-gradient(135deg, oklch(94% 0.015 200) 0%, oklch(92% 0.02 160) 100%)',
        }}
      >
        <iframe
          title="Mappa lotto"
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.006}%2C${lng + 0.01}%2C${lat + 0.006}&layer=mapnik&marker=${lat}%2C${lng}`}
          style={{
            width: '100%',
            height: '100%',
            border: 0,
            display: 'block',
          }}
          loading="lazy"
        />
      </div>
      <div
        style={{
          padding: 'var(--sp-2) var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 'var(--fs-11)',
        }}
      >
        {isLoading && <span className="muted">Calcolo POI…</span>}
        {metro && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 'var(--sp-2)',
            }}
          >
            <span>
              <span aria-hidden style={{ marginRight: 4 }}>Ⓜ</span>
              {metro.label}
            </span>
            <span className="muted" style={{ fontFamily: 'var(--font-mono)' }}>
              {metro.distanza_m} m
            </span>
          </div>
        )}
        {scuole > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 'var(--sp-2)',
            }}
          >
            <span>
              <span aria-hidden style={{ marginRight: 4 }}>🏫</span>
              Scuole &lt; 1km
            </span>
            <span className="muted" style={{ fontFamily: 'var(--font-mono)' }}>
              {scuole}
            </span>
          </div>
        )}
        <a
          href={osmUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 'var(--fs-11)',
            marginTop: 4,
            color: 'var(--accent-ink)',
          }}
        >
          Espandi mappa →
        </a>
      </div>
    </div>
  )
}

function AstaSection({ p }: { p: PropertyRow }) {
  const cust = p.custode ?? null
  // Tentativi previous timeline (use ribasso_storico JSONB).
  const ribasso = (p.vendita?.ribasso_storico ?? []) as Array<{
    date?: string
    base?: number
    esito?: string
  }>
  const ribassoPct = p.vendita?.ribasso_cumulato_pct
  const valorePerizia = p.vendita?.valore_perizia
  const tentativo = p.vendita?.tentativo_corrente
  const tentativiTot = p.vendita?.tentativi_totali

  // Compose current + next predicted step for timeline.
  // Next reduction is derived from history when possible:
  //   1. average % reduction between consecutive ribasso_storico steps;
  //   2. fallback 25% (standard italian art. 591-bis cpc default per
  //      esperimenti successivi when judge non specifica diverso).
  const histBases = ribasso
    .map((r) => r.base)
    .filter((b): b is number => typeof b === 'number' && b > 0)
  let nextReductionPct = 0.25 // 25% fallback
  if (histBases.length >= 2) {
    const reductions: number[] = []
    for (let i = 1; i < histBases.length; i++) {
      const prev = histBases[i - 1]
      const curr = histBases[i]
      if (prev > 0) reductions.push((prev - curr) / prev)
    }
    if (reductions.length > 0) {
      nextReductionPct =
        reductions.reduce((a, b) => a + b, 0) / reductions.length
    }
  }
  const currentStep =
    p.prezzo_base != null
      ? { date: p.data_asta ?? null, base: p.prezzo_base, esito: 'attuale' }
      : null
  const nextStep =
    p.prezzo_base != null
      ? {
          date: null,
          base: Math.round(p.prezzo_base * (1 - nextReductionPct)),
          esito: 'previsto',
          predictedReductionPct: Math.round(nextReductionPct * 100),
        }
      : null
  const timeline = [
    ...ribasso.map((r) => ({
      date: r.date ?? null,
      base: r.base ?? null,
      esito: r.esito ?? 'storico',
    })),
    currentStep,
    nextStep,
  ].filter(Boolean) as Array<{
    date: string | null
    base: number | null
    esito: string
    predictedReductionPct?: number
  }>

  // Helper for sub-row with secondary line.
  const offMinPct =
    p.prezzo_base && p.offerta_minima
      ? Math.round((p.offerta_minima / p.prezzo_base) * 100)
      : null
  const cauzionePct =
    p.prezzo_base && p.cauzione
      ? Math.round((p.cauzione / p.prezzo_base) * 100)
      : null

  return (
    <section className="section" id="asta">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>⚖</span>
          Asta &amp; procedura
          {p.rge && (
            <span
              className="tag code"
              style={{ marginLeft: 8, fontSize: 'var(--fs-11)' }}
            >
              RGE {p.rge}
            </span>
          )}
        </div>
        {p.source_url && (
          <div className="actions">
            <a
              href={p.source_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 'var(--fs-12)',
                color: 'var(--accent-ink)',
              }}
            >
              Apri portale vendite ↗
            </a>
          </div>
        )}
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        {/* FONTI --- dedup cross-portale: tutti i link sorgente */}
        {(p.source_links?.length ?? 0) > 1 && (
          <KVSection title="Fonti">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--sp-2)',
                padding: '4px',
              }}
            >
              {p.source_links!.map((sl, i) => (
                <a
                  key={`${sl.source}-${sl.external_ref}-${i}`}
                  href={sl.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tag"
                  style={{
                    fontSize: 'var(--fs-12)',
                    textDecoration: sl.url ? 'underline' : 'none',
                    opacity: sl.url ? 1 : 0.6,
                  }}
                >
                  {inferGestore(sl.source)} ↗
                </a>
              ))}
            </div>
          </KVSection>
        )}
        {/* PROCEDURA --- 2-col split */}
        <KVSection title="Procedura">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--sp-3)',
            }}
          >
            <KVTable
              rows={[
                ['RGE', p.rge ?? '—'],
                ['Tipo procedura', p.tipo_procedura ?? '—'],
                [
                  'Custode',
                  cust?.nome
                    ? cust.studio
                      ? `${cust.studio}`
                      : cust.nome
                    : '—',
                ],
              ]}
            />
            <KVTable
              rows={[
                ['Tribunale', p.tribunale ?? '—'],
                ['N. lotto', p.lotto ?? 'Lotto unico'],
                ['Giudice', p.giudice ?? '—'],
                ['Delegato vendita', p.delegato ?? '—'],
              ]}
            />
          </div>
          {(cust?.telefono || cust?.email) && (
            <div
              className="muted"
              style={{
                fontSize: 'var(--fs-11)',
                marginTop: 4,
                paddingLeft: 4,
              }}
            >
              {cust.telefono && <span>{cust.telefono}</span>}
              {cust.telefono && cust.email && <span> · </span>}
              {cust.email && <span>{cust.email}</span>}
            </div>
          )}
        </KVSection>

        {/* VENDITA --- 2-col split with secondary lines */}
        <KVSection title="Vendita">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--sp-3)',
            }}
          >
            <div>
              <RowKVRich
                label="Modalità"
                value={p.modalita_vendita ?? '—'}
                sub={
                  p.termine_offerte
                    ? `scadenza offerte ${fmtDateTime(p.termine_offerte)}`
                    : null
                }
              />
              <RowKVRich
                label="Termine offerte"
                value={fmtDateTime(p.termine_offerte)}
                sub={null}
                accent={isUrgent(p.termine_offerte)}
              />
              <RowKVRich
                label="Offerta minima"
                value={fmtMoney(p.offerta_minima)}
                sub={offMinPct != null ? `${offMinPct}% prezzo base` : null}
              />
              <RowKVRich
                label="Cauzione"
                value={fmtMoney(p.cauzione)}
                sub={
                  cauzionePct != null
                    ? `${cauzionePct}% prezzo base · fideiussione o assegno`
                    : null
                }
              />
              {p.source_url && (
                <RowKVRich
                  label="URL avviso"
                  value=""
                  sub={null}
                  link={p.source_url}
                />
              )}
            </div>
            <div>
              <RowKVRich
                label="Data & ora"
                value={fmtDateTime(p.data_asta)}
                sub={relativeAsta(p.data_asta)}
                accent={isUrgent(p.data_asta)}
              />
              <RowKVRich
                label="Gestore vendite"
                value={inferGestore(p.source)}
                sub={p.source === 'pvp' ? 'portale PVP' : null}
              />
              <RowKVRich
                label="Prezzo base"
                value={fmtMoney(p.prezzo_base)}
                sub={
                  ribassoPct != null && valorePerizia != null
                    ? `${ribassoPct > 0 ? '+' : ''}${ribassoPct.toFixed(0)}% vs perizia originaria ${fmtMoney(valorePerizia)}`
                    : null
                }
              />
              <RowKVRich
                label="Rilancio minimo"
                value={fmtMoney(p.rilancio_min)}
                sub={null}
              />
            </div>
          </div>
        </KVSection>

        {/* TENTATIVI PRECEDENTI --- timeline grafica */}
        {timeline.length > 0 && (
          <KVSection
            title={`Tentativi precedenti · ${ribasso.length}`}
            actionsRight={
              ribassoPct != null ? (
                <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
                  Ribasso cumulato{' '}
                  <strong style={{ color: 'var(--risk-ink, #b91c1c)' }}>
                    {ribassoPct > 0 ? '+' : ''}
                    {ribassoPct.toFixed(0)}%
                  </strong>
                </span>
              ) : null
            }
          >
            <TentativiTimeline
              steps={timeline}
              currentIdx={ribasso.length}
              tentativo={tentativo}
              tentativiTot={tentativiTot}
            />
          </KVSection>
        )}
      </div>
    </section>
  )
}

function isUrgent(iso: string | null | undefined): boolean {
  if (!iso) return false
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  const diff = d.getTime() - Date.now()
  return diff > 0 && diff < 7 * 86_400_000
}

function relativeAsta(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  const diff = d.getTime() - now.getTime()
  if (diff < 0) return 'passata'
  const calDays = calendarDaysUntil(d, now)
  if (calDays === 0) {
    const hours = Math.floor((diff / 3_600_000) % 24)
    const mins = Math.floor((diff / 60_000) % 60)
    if (hours === 0) return `Oggi · ${mins}m`
    return `Oggi · ${hours}h ${mins}m`
  }
  const timeStr = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  if (calDays === 1) return `Domani · ${timeStr}`
  if (calDays < 7) return `Tra ${calDays}g`
  return null
}

function inferGestore(source: string | null | undefined): string {
  if (!source) return '—'
  switch (source.toLowerCase()) {
    case 'pvp':
      return 'PVP · giustizia.it'
    case 'astegiudiziarie':
    case 'astalegale':
      return 'Astalegale.net'
    case 'quimmo':
      return 'Quimmo'
    default:
      return source
  }
}

function RowKVRich({
  label,
  value,
  sub,
  accent,
  link,
}: {
  label: string
  value: string
  sub: string | null
  accent?: boolean
  link?: string
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        columnGap: 'var(--sp-2)',
        padding: '6px 0',
        borderBottom: '1px solid var(--line)',
        alignItems: 'baseline',
      }}
    >
      <div
        style={{
          fontSize: 'var(--fs-10)',
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
        }}
      >
        {label}
      </div>
      <div>
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 'var(--fs-12)',
              color: 'var(--accent-ink)',
              wordBreak: 'break-all',
            }}
          >
            {link}
          </a>
        ) : (
          <div
            style={{
              fontSize: 'var(--fs-13)',
              color: accent ? 'var(--risk-ink, #b91c1c)' : 'var(--ink-1)',
              fontWeight: accent ? 'var(--fw-semibold)' : 'var(--fw-regular)',
            }}
          >
            {value || '—'}
          </div>
        )}
        {sub && (
          <div
            className="muted"
            style={{
              fontSize: 'var(--fs-11)',
              marginTop: 2,
              color: accent ? 'var(--risk-ink, #b91c1c)' : 'var(--ink-3)',
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  )
}

function TentativiTimeline({
  steps,
  currentIdx,
  tentativo,
  tentativiTot,
}: {
  steps: Array<{
    date: string | null
    base: number | null
    esito: string
    predictedReductionPct?: number
  }>
  currentIdx: number
  tentativo: number | null | undefined
  tentativiTot: number | null | undefined
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
        gap: 4,
        marginTop: 'var(--sp-2)',
        position: 'relative',
      }}
    >
      {steps.map((s, i) => {
        const isCurrent = i === currentIdx
        const isFuture = s.esito === 'previsto'
        const dotColor = isFuture
          ? 'var(--ink-4, #d1d5db)'
          : isCurrent
            ? 'var(--accent, #f97316)'
            : 'var(--ok, #16a34a)'
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              position: 'relative',
            }}
          >
            {/* Connector line */}
            {i < steps.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  top: 11,
                  left: '50%',
                  width: '100%',
                  height: 2,
                  background: isFuture
                    ? 'repeating-linear-gradient(90deg, var(--ink-4) 0 4px, transparent 4px 8px)'
                    : 'var(--line)',
                  zIndex: 0,
                }}
              />
            )}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: dotColor,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--fs-10)',
                fontWeight: 'var(--fw-semibold)',
                zIndex: 1,
                border: isCurrent ? '2px solid var(--accent-soft, #fff7ed)' : 'none',
                boxShadow: isCurrent ? '0 0 0 2px var(--accent)' : 'none',
              }}
            >
              {i + 1}
            </div>
            <div
              className="muted"
              style={{
                fontSize: 'var(--fs-10)',
                fontFamily: 'var(--font-mono)',
                textAlign: 'center',
              }}
              title={
                isFuture && s.predictedReductionPct != null
                  ? `Previsione: -${s.predictedReductionPct}% (media riduzione storica)`
                  : undefined
              }
            >
              {s.date
                ? fmtDate(s.date)
                : isFuture
                  ? `previsto −${s.predictedReductionPct ?? 25}%`
                  : '—'}
            </div>
            <div
              style={{
                fontSize: 'var(--fs-12)',
                fontWeight: isCurrent
                  ? 'var(--fw-semibold)'
                  : 'var(--fw-regular)',
                color: isCurrent ? 'var(--accent-ink)' : 'var(--ink-2)',
                textAlign: 'center',
              }}
            >
              {s.base != null ? fmtMoney(s.base) : '—'}
            </div>
          </div>
        )
      })}
      {tentativo != null && tentativiTot != null && (
        <div
          className="muted"
          style={{
            position: 'absolute',
            top: -22,
            right: 0,
            fontSize: 'var(--fs-10)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {tentativo}/{tentativiTot}
        </div>
      )}
    </div>
  )
}

type SopralluogoItem = {
  id: string
  short_code?: number
  kind?: string
  scheduled_at: string | null
  status: string
  outcome?: string | null
  custode: string | null
  notes: string | null
  client_name?: string | null
  client_short_code?: number | null
  owner_name?: string | null
}

function SopralluoghiSection({
  items,
  propertyId,
  propertyLabel,
  block,
}: {
  items: SopralluogoItem[]
  propertyId: string
  propertyLabel: string
  block: string | null
}) {
  const qc = useQueryClient()
  const [newOpen, setNewOpen] = useState(false)
  const blockMut = useMutation({
    mutationFn: (reason: SopralluogoBlockReason | null) =>
      setSopralluogoBlock(propertyId, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['property', propertyId] })
      void qc.invalidateQueries({ queryKey: ['properties'] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
    },
  })
  const blocked = !!block
  const effettuati = items.filter((a) =>
    ['completato', 'effettuato', 'eseguito'].includes(
      (a.status || '').toLowerCase(),
    ),
  ).length
  const fissati = items.filter((a) =>
    ['fissato', 'programmato', 'pianificato'].includes(
      (a.status || '').toLowerCase(),
    ),
  ).length

  return (
    <section className="section" id="sopralluoghi">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>📷</span>
          Sopralluoghi
          {(effettuati > 0 || fissati > 0) && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {effettuati > 0 && `${effettuati} effettuati`}
              {effettuati > 0 && fissati > 0 && ' · '}
              {fissati > 0 && `${fissati} fissato${fissati > 1 ? 'i' : ''}`}
            </span>
          )}
        </div>
        <div
          className="actions"
          style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}
        >
          {!blocked && (
            <select
              value=""
              disabled={blockMut.isPending}
              title="Blocca i sopralluoghi su questo lotto"
              onChange={(e) => {
                const r = e.target.value
                if (r) blockMut.mutate(r as SopralluogoBlockReason)
              }}
              style={{ fontSize: 'var(--fs-12)' }}
            >
              <option value="">🚫 Blocca…</option>
              {Object.entries(SOPRALLUOGO_BLOCK_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className="btn sm"
            disabled={blocked}
            title={
              blocked
                ? 'Lotto bloccato: richiesta sopralluogo disabilitata'
                : ''
            }
            onClick={() => setNewOpen(true)}
          >
            + Nuovo sopralluogo
          </button>
        </div>
      </div>
      {blocked && (
        <div
          role="alert"
          style={{
            margin: 'var(--sp-2) var(--sp-3) 0',
            padding: 'var(--sp-2) var(--sp-3)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--risk-soft)',
            border: '1px solid var(--risk-line)',
            color: 'var(--risk-ink)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
            fontSize: 'var(--fs-12)',
          }}
        >
          <span>
            🚫 Sopralluoghi <b>bloccati</b>:{' '}
            <b>{SOPRALLUOGO_BLOCK_LABEL[block ?? ''] ?? block}</b>. Le richieste
            sono disabilitate.
          </span>
          <button
            type="button"
            className="btn sm"
            style={{ marginLeft: 'auto' }}
            disabled={blockMut.isPending}
            onClick={() => blockMut.mutate(null)}
          >
            Sblocca
          </button>
        </div>
      )}
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        {items.length === 0 && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun sopralluogo registrato per questo lotto.
          </div>
        )}
        {items.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
            }}
          >
            {items.map((a) => (
              <SopralluogoRow key={a.id} item={a} />
            ))}
          </ul>
        )}
      </div>
      <AppointmentFormModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        initialValue={{ property_id: propertyId, property_label: propertyLabel }}
      />
    </section>
  )
}

function SopralluogoRow({ item }: { item: SopralluogoItem }) {
  const dt = item.scheduled_at ? new Date(item.scheduled_at) : null
  const dd = dt ? String(dt.getDate()).padStart(2, '0') : '—'
  const mm = dt
    ? dt
        .toLocaleString('it-IT', { month: 'short' })
        .replace('.', '')
        .toUpperCase()
    : ''
  const hhmm = dt
    ? `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
    : ''
  const status = (item.status || '').toLowerCase()
  const statusLabel = item.status?.replace(/_/g, ' ') ?? '—'
  const statusVariant = (() => {
    if (['completato', 'effettuato', 'eseguito'].includes(status)) return 'ok'
    if (['fissato', 'programmato', 'pianificato'].includes(status))
      return 'accent'
    if (['parziale', 'no_accesso'].includes(status)) return 'warn'
    if (['annullato', 'saltato'].includes(status)) return 'risk'
    return 'outline'
  })()
  const kindLabel = item.kind?.replace(/_/g, ' ') ?? 'Sopralluogo'

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '64px 1fr auto',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-2) var(--sp-3)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface)',
      }}
    >
      {/* Date col */}
      <div
        style={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          paddingTop: 2,
        }}
      >
        <div
          style={{
            fontSize: 'var(--fs-18)',
            fontWeight: 'var(--fw-semibold)',
            lineHeight: 1,
          }}
        >
          {dd}
        </div>
        <div
          className="muted"
          style={{
            fontSize: 'var(--fs-10)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
          }}
        >
          {mm}
        </div>
        {hhmm && (
          <div
            className="muted"
            style={{
              fontSize: 'var(--fs-10)',
              fontFamily: 'var(--font-mono)',
              marginTop: 2,
            }}
          >
            {hhmm}
          </div>
        )}
      </div>

      {/* Body col */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
          }}
        >
          <span className={`chip ${statusVariant}`} style={{ fontSize: 'var(--fs-11)' }}>
            <span
              className="dot"
              style={{
                background:
                  statusVariant === 'ok'
                    ? 'var(--ok)'
                    : statusVariant === 'accent'
                      ? 'var(--accent)'
                      : statusVariant === 'warn'
                        ? 'var(--warn)'
                        : statusVariant === 'risk'
                          ? 'var(--risk)'
                          : 'var(--ink-3)',
              }}
            />
            {statusLabel}
          </span>
          <span
            style={{
              fontSize: 'var(--fs-13)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            {kindLabel}
            {item.client_name && ` · ${item.client_name}`}
          </span>
        </div>
        <div
          className="muted"
          style={{
            fontSize: 'var(--fs-11)',
            display: 'flex',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
          }}
        >
          {item.client_short_code != null && (
            <span>
              Cliente{' '}
              <span className="tag code" style={{ fontSize: 'var(--fs-10)' }}>
                C_{String(item.client_short_code).padStart(5, '0')}
              </span>
            </span>
          )}
          {item.owner_name && <span>Consulente {item.owner_name}</span>}
          {item.custode && <span>Custode {item.custode}</span>}
        </div>
        {item.notes && (
          <div
            style={{
              fontSize: 'var(--fs-12)',
              color: 'var(--ink-2)',
              marginTop: 4,
            }}
          >
            {item.notes}
          </div>
        )}
      </div>

      {/* Outcome col */}
      <div
        style={{
          minWidth: 110,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
        }}
      >
        {item.outcome && (
          <span
            className="muted"
            style={{ fontSize: 'var(--fs-10)' }}
          >
            esito{' '}
            <strong style={{ color: 'var(--ok-ink, #15803d)' }}>
              {item.outcome.replace(/_/g, ' ')}
            </strong>
          </span>
        )}
      </div>
    </li>
  )
}

function MatchingSection({ propertyId }: { propertyId: string }) {
  const matchesQ = usePropertyMatches(propertyId, { limit: 10, min_score: 0 })
  const allItems = matchesQ.data?.items ?? []
  const totalCandidates = matchesQ.data?.total_candidates ?? 0
  const totalMatches = matchesQ.data?.total_matches ?? 0

  // Split active vs excluded.
  const active = allItems.filter((m) => !m.breakdown.rejected).slice(0, 5)
  const excluded = allItems.filter((m) => m.breakdown.rejected)

  // Aggregate exclusion reasons.
  const exclusionStats = useMemo(() => {
    const stats: Record<string, number> = {}
    for (const m of excluded) {
      const r = m.breakdown.rejected
      if (r) stats[r] = (stats[r] ?? 0) + 1
    }
    return stats
  }, [excluded])

  return (
    <section className="section" id="matching">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>👥</span>
          Matching clienti
          {(active.length > 0 || excluded.length > 0) && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {active.length} attivi
              {excluded.length > 0 && ` · ${excluded.length} esclusi`}
            </span>
          )}
        </div>
        <div className="actions">
          <Link
            to="/lotti/$id/match"
            params={{ id: propertyId }}
            search={{} as never}
            style={{
              fontSize: 'var(--fs-12)',
              color: 'var(--accent-ink)',
            }}
          >
            🔍 Esplora workbench
          </Link>
        </div>
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        {matchesQ.isLoading && (
          <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
            Caricamento match…
          </div>
        )}
        {!matchesQ.isLoading && totalCandidates === 0 && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun cliente candidato — apri il workbench per filtri allargati.
          </div>
        )}
        {active.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
            }}
          >
            {active.map((m) => (
              <MatchRow key={m.client.id} m={m} />
            ))}
          </ul>
        )}
        {/* Excluded summary */}
        {excluded.length > 0 && (
          <div
            style={{
              marginTop: 'var(--sp-3)',
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'var(--bg-2)',
              borderRadius: 'var(--r-sm)',
              fontSize: 'var(--fs-12)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
            }}
          >
            <span>
              <strong>{excluded.length} clienti</strong> esclusi automaticamente
              {Object.keys(exclusionStats).length > 0 && (
                <span className="muted">
                  {' · '}
                  {Object.entries(exclusionStats)
                    .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
                    .join(' · ')}
                </span>
              )}
            </span>
            <Link
              to="/lotti/$id/match"
              params={{ id: propertyId }}
              search={{} as never}
              style={{ fontSize: 'var(--fs-12)', color: 'var(--accent-ink)' }}
            >
              Vedi esclusi →
            </Link>
          </div>
        )}
        {totalMatches > active.length && (
          <div
            className="muted"
            style={{
              marginTop: 'var(--sp-2)',
              fontSize: 'var(--fs-11)',
              textAlign: 'center',
            }}
          >
            +{totalMatches - active.length} altri match attivi · apri workbench
          </div>
        )}
      </div>
    </section>
  )
}

function MatchRow({ m }: { m: MatchForPropertyRow }) {
  const c = m.client
  const score = Math.round(m.score)
  const ini = `${c.cognome[0] ?? ''}${c.nome[0] ?? ''}`.toUpperCase()
  const cCode = `C_${String(c.short_code).padStart(5, '0')}`

  // Score breakdown -> positive/negative chips (heuristic).
  const positives: string[] = []
  const negatives: string[] = []
  if (m.breakdown.budget_fit >= 0.8) positives.push('Budget centrato')
  else if (m.breakdown.budget_fit < 0.3 && m.breakdown.budget_fit > 0)
    negatives.push('Budget fuori range')
  if (m.breakdown.zone_match >= 0.7) positives.push('Zona match')
  else if (m.breakdown.zone_match < 0.3) negatives.push('Zona non preferita')
  if (m.breakdown.semantic >= 0.6) positives.push('Tipologia ok')
  if (m.breakdown.rating_bonus > 0) positives.push('Cliente top rating')

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-2) var(--sp-3)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface)',
        alignItems: 'center',
      }}
    >
      {/* Avatar */}
      <div
        className="avatar md"
        style={{ background: avatarColor(c.id), color: '#fff' }}
      >
        {ini}
      </div>

      {/* Body */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 'var(--fs-13)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            {c.cognome} {c.nome}
          </span>
          <span className="tag code" style={{ fontSize: 'var(--fs-10)' }}>
            {cCode}
          </span>
          {c.tipologia && (
            <span className="chip outline" style={{ fontSize: 'var(--fs-11)' }}>
              {c.tipologia.toUpperCase()}
            </span>
          )}
          {c.priorita && (
            <span
              className={`chip ${c.priorita === 'A' ? 'ok' : c.priorita === 'B' ? 'warn' : 'outline'}`}
              style={{ fontSize: 'var(--fs-11)' }}
            >
              {c.priorita} ·{' '}
              {c.priorita === 'A'
                ? 'ATTIVO'
                : c.priorita === 'B'
                  ? 'MEDIO'
                  : 'BASSO'}
            </span>
          )}
        </div>
        <div
          className="muted"
          style={{
            fontSize: 'var(--fs-11)',
            display: 'flex',
            gap: 'var(--sp-2)',
            flexWrap: 'wrap',
          }}
        >
          {c.budget != null && <span>Budget {fmtMoneyK(c.budget)}</span>}
          {c.zone_tag && c.zone_tag.length > 0 && (
            <span>Zone {c.zone_tag.join(', ')}</span>
          )}
          {c.consultant_name && <span>Consulente {c.consultant_name}</span>}
        </div>
        {(positives.length > 0 || negatives.length > 0) && (
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'wrap',
              marginTop: 2,
            }}
          >
            {positives.map((t) => (
              <span
                key={`p-${t}`}
                className="chip ok"
                style={{ fontSize: 'var(--fs-10)' }}
              >
                + {t}
              </span>
            ))}
            {negatives.map((t) => (
              <span
                key={`n-${t}`}
                className="chip risk"
                style={{ fontSize: 'var(--fs-10)' }}
              >
                − {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Score ring */}
      <div
        style={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <ScoreRing value={score} />
      </div>
    </li>
  )
}

function ScoreRing({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value))
  const r = 18
  const c = 2 * Math.PI * r
  const dashOffset = c * (1 - pct / 100)
  const color =
    pct >= 80
      ? 'var(--ok, #16a34a)'
      : pct >= 60
        ? 'var(--accent, #f97316)'
        : 'var(--risk, #dc2626)'
  return (
    <div
      style={{
        width: 48,
        height: 48,
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <svg
        width={48}
        height={48}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={24}
          cy={24}
          r={r}
          fill="none"
          stroke="var(--line)"
          strokeWidth={3}
        />
        <circle
          cx={24}
          cy={24}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-13)',
          fontWeight: 'var(--fw-semibold)',
          color: color,
          position: 'relative',
        }}
      >
        {value}
      </span>
    </div>
  )
}

function fmtMoneyK(v: number | null | undefined): string {
  if (v == null) return '—'
  if (v >= 1_000_000) return `€ ${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1000) return `€ ${(v / 1000).toFixed(0)}k`
  return `€ ${v}`
}

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const hue = Math.abs(h) % 360
  return `oklch(58% 0.14 ${hue})`
}

function DocumentiSection({
  propertyId,
  canEdit,
  openPickerNonce,
}: {
  propertyId: string
  canEdit: boolean
  openPickerNonce: number
}) {
  // Reuse Cliente Documenti component but pass property_id — backend supports
  // both scopes via the same /documents endpoint. Type expects clientId so we
  // wrap with a small adapter via prop name; simplest: render section inline
  // mirroring same UX. For now, we mount a property-scoped copy.
  return (
    <PropertyDocumenti
      propertyId={propertyId}
      canEdit={canEdit}
      openPickerNonce={openPickerNonce}
    />
  )
}

// Re-use ClienteDocumenti pattern but scoped to property. Inline minimal.

const KIND_OPTIONS: { value: DocumentKind; label: string }[] = [
  // 'ctu' e 'perizia' = stesso documento (perizia di stima del CTU). Voce unica
  // nel menu upload ('perizia'); 'ctu' resta per i doc già caricati come tali.
  { value: 'ctu', label: 'Perizia / CTU' },
  { value: 'perizia', label: 'Perizia / CTU' },
  { value: 'planimetria', label: 'Planimetria' },
  { value: 'avviso_vendita', label: 'Avviso vendita' },
  { value: 'ordinanza', label: 'Ordinanza' },
  { value: 'bonifico_cauzione', label: 'Bonifico/cauzione' },
  { value: 'domanda_partecipazione', label: 'Domanda partecipazione' },
  { value: 'documento_identita', label: 'Documento identità' },
  { value: 'codice_fiscale', label: 'Codice fiscale' },
  { value: 'procura', label: 'Procura' },
  { value: 'dichiarazione', label: 'Dichiarazione' },
  { value: 'firma_digitale', label: 'Firma digitale' },
  { value: 'altro', label: 'Altro' },
]

type DocTab = 'tutti' | 'tribunale' | 'scd' | 'sopralluogo' | 'cliente'

const TRIBUNALE_KINDS = new Set([
  'ctu',
  'perizia',
  'planimetria',
  'avviso_vendita',
  'ordinanza',
])
const SCD_KINDS = new Set(['scheda_fattibilita', 'visura', 'ape', 'altro_scd'])
const SOPRALLUOGO_KINDS = new Set([
  'foto_sopralluogo',
  'video_sopralluogo',
  'report_sopralluogo',
])
const CLIENTE_KINDS = new Set([
  'bonifico_cauzione',
  'domanda_partecipazione',
  'documento_identita',
  'codice_fiscale',
  'procura',
  'dichiarazione',
  'firma_digitale',
])

function docBucket(kind: string): DocTab {
  if (TRIBUNALE_KINDS.has(kind)) return 'tribunale'
  if (SCD_KINDS.has(kind)) return 'scd'
  if (SOPRALLUOGO_KINDS.has(kind)) return 'sopralluogo'
  if (CLIENTE_KINDS.has(kind)) return 'cliente'
  return 'tutti'
}

function PropertyDocumenti({
  propertyId,
  canEdit,
  openPickerNonce = 0,
}: {
  propertyId: string
  canEdit: boolean
  openPickerNonce?: number
}) {
  const { data, isLoading, isError, error } = useDocumentList({
    property_id: propertyId,
    page_size: 100,
  })
  const uploadMut = useUploadDocument()
  const deleteMut = useDeleteDocument()
  const rekindMut = useUpdateDocumentKind()
  const urlMut = useFetchDocumentUrl()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [kind, setKind] = useState<DocumentKind>('perizia')
  const [uploadErr, setUploadErr] = useState<string>('')
  const [tab, setTab] = useState<DocTab>('tutti')

  useEffect(() => {
    if (openPickerNonce > 0 && canEdit && fileRef.current) {
      document
        .getElementById('documenti')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      fileRef.current.click()
    }
  }, [openPickerNonce, canEdit])

  const items = data?.items ?? []
  const total = data?.total ?? 0

  const counts = useMemo(() => {
    const c: Record<DocTab, number> = {
      tutti: items.length,
      tribunale: 0,
      scd: 0,
      sopralluogo: 0,
      cliente: 0,
    }
    for (const d of items) {
      const b = docBucket(d.kind)
      if (b !== 'tutti') c[b] += 1
    }
    return c
  }, [items])

  const filteredItems = useMemo(() => {
    if (tab === 'tutti') return items
    return items.filter((d) => docBucket(d.kind) === tab)
  }, [items, tab])

  // SCD progress: derive status from presence of docs in each bucket.
  const scdRicerca: {
    complete: boolean
    count: number
    summary: string
  } = {
    complete: counts.tribunale >= 4,
    count: counts.tribunale,
    summary:
      counts.tribunale > 0
        ? `${counts.tribunale} doc · CTU + perizia + planimetrie + ord. vendita + visure`
        : 'In attesa documenti tribunale',
  }
  const scdDoc: {
    complete: boolean
    summary: string
  } = {
    complete: counts.scd >= 1,
    summary:
      counts.scd > 0
        ? `${counts.scd} scheda${counts.scd > 1 ? 'e' : ''} · analista`
        : 'Scheda fattibilità non ancora caricata',
  }
  const scdSopralluogo: {
    complete: boolean
    summary: string
  } = {
    complete: counts.sopralluogo >= 1,
    summary:
      counts.sopralluogo > 0
        ? `${counts.sopralluogo} file · foto + report`
        : 'Sopralluogo non ancora documentato',
  }

  const totalSizeBytes = items.reduce(
    (acc, d) => acc + (d.size_bytes ?? 0),
    0,
  )

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadErr('')
    try {
      await uploadMut.mutateAsync({ file, kind, property_id: propertyId })
      if (fileRef.current) fileRef.current.value = ''
    } catch (err) {
      setUploadErr(err instanceof Error ? err.message : 'Errore upload')
    }
  }

  async function onOpen(doc: DocumentRow) {
    try {
      const res = await urlMut.mutateAsync(doc.id)
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch {
      /* silent */
    }
  }

  async function onDelete(doc: DocumentRow) {
    if (!confirm(`Eliminare "${doc.filename}"?`)) return
    try {
      await deleteMut.mutateAsync(doc.id)
    } catch {
      /* silent */
    }
  }

  async function onRekind(doc: DocumentRow, kind: DocumentKind) {
    try {
      await rekindMut.mutateAsync({ id: doc.id, kind })
    } catch {
      /* silent */
    }
  }

  return (
    <section className="section" id="documenti">
      <div className="section-head">
        <div className="title">
          <span aria-hidden style={{ marginRight: 6 }}>📁</span>
          Documenti &amp; SCD
          {total > 0 && (
            <span
              className="muted"
              style={{
                marginLeft: 8,
                fontSize: 'var(--fs-11)',
                fontWeight: 'var(--fw-regular)',
              }}
            >
              {total} file · {fmtBytes(totalSizeBytes)}
            </span>
          )}
        </div>
        {canEdit && (
          <div className="actions" style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as DocumentKind)}
              className="filter-select"
              style={{ fontSize: 'var(--fs-12)' }}
            >
              {KIND_OPTIONS.filter((o) => o.value !== 'ctu').map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploadMut.isPending}
            >
              {uploadMut.isPending ? 'Caricamento…' : '⬇ Carica'}
            </button>
            <input
              ref={fileRef}
              type="file"
              onChange={onPickFile}
              style={{ display: 'none' }}
              accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx,.zip"
            />
          </div>
        )}
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        {/* 3 SCD progress cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 'var(--sp-2)',
            marginBottom: 'var(--sp-3)',
          }}
        >
          <ScdCard
            title="SCD Ricerca"
            complete={scdRicerca.complete}
            summary={scdRicerca.summary}
            note={scdRicerca.complete ? 'Completa' : 'Parziale'}
          />
          <ScdCard
            title="SCD Doc"
            complete={scdDoc.complete}
            summary={scdDoc.summary}
            note={scdDoc.complete ? 'Completa · approvata' : 'Pending'}
          />
          <ScdCard
            title="SCD Sopralluogo"
            complete={scdSopralluogo.complete}
            summary={scdSopralluogo.summary}
            note={scdSopralluogo.complete ? 'Completo' : 'Parziale'}
          />
        </div>

        {/* Tab filtri */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            borderBottom: '1px solid var(--line)',
            marginBottom: 'var(--sp-2)',
            fontSize: 'var(--fs-12)',
          }}
        >
          {(
            [
              ['tutti', 'Tutti'],
              ['tribunale', 'Tribunale'],
              ['scd', 'SCD'],
              ['sopralluogo', 'Sopralluogo'],
              ['cliente', 'Cliente'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              style={{
                background: 'none',
                border: 'none',
                padding: '6px 4px',
                cursor: 'pointer',
                color: tab === k ? 'var(--ink-1)' : 'var(--ink-3)',
                fontWeight:
                  tab === k ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                borderBottom:
                  tab === k
                    ? '2px solid var(--accent)'
                    : '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {label}
              {counts[k] > 0 && (
                <span
                  className="ct"
                  style={{
                    background:
                      tab === k ? 'var(--accent-soft)' : 'var(--bg-2)',
                    color: tab === k ? 'var(--accent-ink)' : 'var(--ink-3)',
                    padding: '1px 6px',
                    borderRadius: 'var(--r-pill)',
                    fontSize: 'var(--fs-10)',
                  }}
                >
                  {counts[k]}
                </span>
              )}
            </button>
          ))}
        </div>

        {uploadErr && (
          <div className="error" style={{ marginBottom: 'var(--sp-2)' }}>
            {uploadErr}
          </div>
        )}
        {isLoading && (
          <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
            Caricamento…
          </div>
        )}
        {isError && (
          <div className="error">
            {error?.message ?? 'Errore caricamento documenti'}
          </div>
        )}
        {!isLoading && !isError && filteredItems.length === 0 && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun documento in questa categoria.
          </div>
        )}
        {filteredItems.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            {filteredItems.map((doc) => (
              <DocumentRowItem
                key={doc.id}
                doc={doc}
                canEdit={canEdit}
                onOpen={onOpen}
                onDelete={onDelete}
                onRekind={onRekind}
                deleting={deleteMut.isPending}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function ScdCard({
  title,
  complete,
  summary,
  note,
}: {
  title: string
  complete: boolean
  summary: string
  note: string
}) {
  return (
    <div
      style={{
        border: `1px solid ${complete ? 'var(--ok, #16a34a)' : 'var(--line)'}`,
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-2) var(--sp-3)',
        background: complete
          ? 'oklch(98% 0.025 145 / 0.4)'
          : 'var(--bg-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span
          className="muted"
          style={{
            fontSize: 'var(--fs-10)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          {title}
        </span>
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: complete ? 'var(--ok, #16a34a)' : 'var(--bg-3, #e5e7eb)',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          {complete ? '✓' : '–'}
        </span>
      </div>
      <div
        style={{
          fontSize: 'var(--fs-14)',
          fontWeight: 'var(--fw-semibold)',
        }}
      >
        {note}
      </div>
      <div className="muted" style={{ fontSize: 'var(--fs-11)' }}>
        {summary}
      </div>
    </div>
  )
}

function DocumentRowItem({
  doc,
  canEdit,
  onOpen,
  onDelete,
  onRekind,
  deleting,
}: {
  doc: DocumentRow
  canEdit: boolean
  onOpen: (d: DocumentRow) => void
  onDelete: (d: DocumentRow) => void
  onRekind: (d: DocumentRow, kind: DocumentKind) => void
  deleting: boolean
}) {
  const [localKind, setLocalKind] = useState<DocumentKind>(doc.kind as DocumentKind)
  const ext = doc.filename.split('.').pop()?.toUpperCase() ?? 'FILE'
  const isImg = ['JPG', 'JPEG', 'PNG', 'HEIC'].includes(ext)
  const isPdf = ext === 'PDF'
  const isZip = ext === 'ZIP'
  const icon = isPdf ? 'PDF' : isImg ? 'JPG' : isZip ? 'ZIP' : ext.slice(0, 3)
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '44px 1fr auto auto auto auto',
        gap: 'var(--sp-2)',
        alignItems: 'center',
        padding: 'var(--sp-2) var(--sp-3)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        fontSize: 'var(--fs-12)',
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          background: 'var(--bg-2)',
          color: 'var(--ink-3)',
          borderRadius: 'var(--r-sm)',
          display: 'grid',
          placeItems: 'center',
          fontSize: 9,
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 0.5,
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 'var(--fw-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.filename}
        </div>
        {canEdit ? (
          <select
            value={localKind}
            onChange={(e) => {
              const k = e.target.value as DocumentKind
              setLocalKind(k)
              onRekind(doc, k)
            }}
            style={{
              fontSize: 'var(--fs-10)',
              color: 'var(--ink-3)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              outline: 'none',
            }}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <div className="muted" style={{ fontSize: 'var(--fs-10)' }}>
            {KIND_OPTIONS.find((o) => o.value === doc.kind)?.label ?? doc.kind}
          </div>
        )}
      </div>
      <span
        className="muted"
        style={{
          fontSize: 'var(--fs-10)',
          fontFamily: 'var(--font-mono)',
          textAlign: 'right',
        }}
      >
        {fmtDate(doc.uploaded_at)}
        <br />
        <span style={{ fontSize: 9 }}>
          {doc.uploaded_by_name ?? 'Sistema'}
        </span>
      </span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-11)',
          color: 'var(--ink-3)',
          minWidth: 60,
          textAlign: 'right',
        }}
      >
        {doc.size_bytes != null ? fmtBytes(doc.size_bytes) : '—'}
      </span>
      <ExtractionBadge status={doc.extraction_status} />
      <span style={{ display: 'flex', gap: 4 }}>
        <button
          type="button"
          className="btn sm ghost"
          onClick={() => onOpen(doc)}
          title="Apri"
          style={{ padding: '2px 6px' }}
        >
          👁
        </button>
        <button
          type="button"
          className="btn sm ghost"
          onClick={() => onOpen(doc)}
          title="Scarica"
          style={{ padding: '2px 6px' }}
        >
          ⬇
        </button>
        {canEdit && (
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => onDelete(doc)}
            title="Elimina"
            style={{ color: 'var(--risk-ink, #b91c1c)', padding: '2px 6px' }}
            disabled={deleting}
          >
            🗑
          </button>
        )}
      </span>
    </li>
  )
}

function ExtractionBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    done: {
      label: 'OCR✓',
      color: 'var(--ok-ink, #15803d)',
      bg: 'oklch(95% 0.04 145)',
    },
    processing: {
      label: 'OCR…',
      color: 'var(--accent-ink)',
      bg: 'var(--accent-soft)',
    },
    pending: {
      label: 'pending',
      color: 'var(--ink-3)',
      bg: 'var(--bg-2)',
    },
    failed: {
      label: 'errore',
      color: 'var(--risk-ink, #b91c1c)',
      bg: 'oklch(96% 0.05 25)',
    },
    n_a: {
      label: 'n/a',
      color: 'var(--ink-4, #9ca3af)',
      bg: 'var(--bg-2)',
    },
    parsed: {
      label: 'parsed',
      color: 'var(--accent-ink)',
      bg: 'var(--accent-soft)',
    },
    tag: {
      label: 'tag',
      color: 'var(--ink-2)',
      bg: 'var(--bg-2)',
    },
    approv: {
      label: 'approv.',
      color: 'var(--ok-ink, #15803d)',
      bg: 'oklch(95% 0.04 145)',
    },
  }
  const s = map[status] ?? map.n_a
  return (
    <span
      style={{
        fontSize: 9,
        padding: '2px 6px',
        borderRadius: 'var(--r-sm)',
        background: s.bg,
        color: s.color,
        fontFamily: 'var(--font-mono)',
        fontWeight: 'var(--fw-semibold)',
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  )
}

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function KVSection({
  title,
  children,
  actionsRight,
}: {
  title: string
  children: React.ReactNode
  actionsRight?: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 'var(--sp-1)',
          gap: 'var(--sp-2)',
        }}
      >
        <div
          className="muted"
          style={{
            fontSize: 'var(--fs-10)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          {title}
        </div>
        {actionsRight}
      </div>
      {children}
    </div>
  )
}

function KVTable({ rows }: { rows: [string, string | number | null | undefined][] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        rowGap: 0,
        columnGap: 'var(--sp-3)',
        alignItems: 'baseline',
      }}
    >
      {rows.map(([label, value], i) => (
        <RowKV
          key={i}
          label={label}
          value={value == null || value === '' ? '—' : String(value)}
        />
      ))}
    </div>
  )
}

function RowKV({ label, value }: { label: string; value: string }) {
  return (
    <>
      <div
        style={{
          fontSize: 'var(--fs-11)',
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          padding: '8px 0',
          borderBottom: '1px solid var(--line)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 'var(--fs-13)',
          padding: '8px 0',
          borderBottom: '1px solid var(--line)',
        }}
      >
        {value}
      </div>
    </>
  )
}

function RegistraAggiudicazioneModal({
  propertyId,
  dataAsta,
  onClose,
}: {
  propertyId: string
  dataAsta: string | null | undefined
  onClose: () => void
}) {
  const navigate = useNavigate()
  const createPart = useCreateParticipation()
  const usersQ = useUsersList()
  const consulenti = usersQ.data?.items ?? []
  const [consultantId, setConsultantId] = useState('')
  const [clientQuery, setClientQuery] = useState('')
  const [clientResults, setClientResults] = useState<ClientRow[]>([])
  const [clientId, setClientId] = useState('')
  const [clientLabel, setClientLabel] = useState('')
  const [esito, setEsito] = useState<'aggiudicata' | 'non_aggiudicata'>(
    'aggiudicata',
  )
  const [prezzo, setPrezzo] = useState('')
  const [dataAgg, setDataAgg] = useState(toLocalDateInputValue(dataAsta))
  const [err, setErr] = useState('')

  useEffect(() => {
    if (clientId) return
    const q = clientQuery.trim()
    if (q.length < 2) {
      setClientResults([])
      return
    }
    let active = true
    const t = setTimeout(() => {
      void fetchClients({ q, page_size: 8 })
        .then((res) => {
          if (active) setClientResults(res.items)
        })
        .catch(() => {
          if (active) setClientResults([])
        })
    }, 250)
    return () => {
      active = false
      clearTimeout(t)
    }
  }, [clientQuery, clientId])

  async function submit() {
    if (!clientId) {
      setErr('Seleziona un cliente')
      return
    }
    setErr('')
    try {
      const part = await createPart.mutateAsync({
        property_id: propertyId,
        client_id: clientId,
        consultant_id: consultantId || undefined,
        status: esito,
        prezzo_aggiudicazione:
          esito === 'aggiudicata' && prezzo ? Number(prezzo) : undefined,
        data_aggiudicazione: dataAgg || undefined,
      })
      onClose()
      void navigate({
        to: '/partecipazioni/$id',
        params: { id: part.id },
      } as never)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore creazione partecipazione')
    }
  }

  const inputStyle = {
    width: '100%',
    padding: 8,
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-sm)',
  } as const

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          width: 'min(460px, 92vw)',
          maxHeight: '88vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <h3 style={{ margin: 0 }}>📋 Registra aggiudicazione</h3>

        {/* Cliente */}
        <div style={{ position: 'relative' }}>
          <label className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            Cliente
          </label>
          {clientId ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                ...inputStyle,
              }}
            >
              <span>{clientLabel}</span>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  setClientId('')
                  setClientLabel('')
                  setClientQuery('')
                }}
              >
                cambia
              </button>
            </div>
          ) : (
            <input
              type="text"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder="Cerca cliente (cognome/nome/email/C_…)"
              autoComplete="off"
              style={inputStyle}
            />
          )}
          {!clientId && clientResults.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 20,
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-sm)',
                maxHeight: 220,
                overflow: 'auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              {clientResults.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setClientId(c.id)
                    setClientLabel(`${c.cognome} ${c.nome}`.trim())
                    setClientResults([])
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid var(--line)',
                    cursor: 'pointer',
                    fontSize: 'var(--fs-13)',
                  }}
                >
                  <strong>{c.cognome}</strong> {c.nome}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Consulente di riferimento */}
        <div>
          <label className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            Consulente di riferimento
          </label>
          <select
            value={consultantId}
            onChange={(e) => setConsultantId(e.target.value)}
            style={inputStyle}
          >
            <option value="">— Io / default —</option>
            {consulenti.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name}
              </option>
            ))}
          </select>
        </div>

        {/* Esito */}
        <div>
          <label className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            Esito
          </label>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 4 }}>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="radio"
                checked={esito === 'aggiudicata'}
                onChange={() => setEsito('aggiudicata')}
              />
              Aggiudicata
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="radio"
                checked={esito === 'non_aggiudicata'}
                onChange={() => setEsito('non_aggiudicata')}
              />
              Non aggiudicata
            </label>
          </div>
        </div>

        {esito === 'aggiudicata' && (
          <div>
            <label className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              Prezzo aggiudicazione (€)
            </label>
            <input
              type="number"
              min={0}
              value={prezzo}
              onChange={(e) => setPrezzo(e.target.value)}
              placeholder="es. 105000"
              style={inputStyle}
            />
          </div>
        )}

        <div>
          <label className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            Data aggiudicazione
          </label>
          <input
            type="date"
            value={dataAgg}
            onChange={(e) => setDataAgg(e.target.value)}
            style={inputStyle}
          />
        </div>

        {err && (
          <div className="error" style={{ fontSize: 'var(--fs-12)' }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn sm ghost" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn sm primary"
            disabled={createPart.isPending}
            onClick={() => void submit()}
          >
            {createPart.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CountdownCard({
  dataAsta,
  propertyId,
  tribunale,
}: {
  dataAsta: string | null | undefined
  propertyId: string
  tribunale: string | null | undefined
}) {
  const navigate = useNavigate()
  const [now, setNow] = useState(Date.now())
  const [aggOpen, setAggOpen] = useState(false)
  useEffect(() => {
    if (!dataAsta) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [dataAsta])

  if (!dataAsta) return null
  const target = new Date(dataAsta).getTime()
  if (Number.isNaN(target)) return null
  const diff = target - now
  if (diff < 0) {
    return (
      <>
        <aside
          style={{
            padding: 'var(--sp-3)',
            background: 'var(--bg-2)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
            Asta passata · {fmtDateTime(dataAsta)}
          </div>
          <button
            type="button"
            className="btn sm primary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setAggOpen(true)}
          >
            📋 Registra aggiudicazione
          </button>
        </aside>
        {aggOpen && (
          <RegistraAggiudicazioneModal
            propertyId={propertyId}
            dataAsta={dataAsta}
            onClose={() => setAggOpen(false)}
          />
        )}
      </>
    )
  }
  const days = Math.floor(diff / 86_400_000)
  const hours = Math.floor((diff / 3_600_000) % 24)
  const mins = Math.floor((diff / 60_000) % 60)
  const secs = Math.floor((diff / 1000) % 60)

  const dt = new Date(dataAsta)
  const dateStr = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`
  const timeStr = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
  const relativeLabel = relativeAsta(dataAsta) ?? fmtDate(dataAsta)
  const tribunaleStr = tribunale ? tribunale.toUpperCase() : null

  return (
    <aside
      className="countdown-card"
      style={{
        padding: 'var(--sp-3)',
        background: 'oklch(20% 0.01 240)',
        color: '#fff',
        borderRadius: 'var(--r-md)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      {/* Decorative accent dot top-right */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'oklch(55% 0.15 50)',
          opacity: 0.6,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 'var(--fs-10)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--accent, #f97316)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent, #f97316)',
            display: 'inline-block',
          }}
        />
        Asta imminente
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 26,
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: '-0.02em',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {dateStr} · {timeStr}
      </div>
      <div
        style={{
          fontSize: 'var(--fs-10)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'oklch(75% 0.01 240)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {relativeLabel}
        {tribunaleStr && ` · ${tribunaleStr}`}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 4,
          paddingTop: 'var(--sp-2)',
          borderTop: '1px solid oklch(30% 0.01 240)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Digit n={days} u="GIORNI" />
        <Digit n={hours} u="ORE" />
        <Digit n={mins} u="MIN" />
        <Digit n={secs} u="SEC" />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 6,
          marginTop: 4,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <button
          type="button"
          onClick={() =>
            void navigate({
              to: '/lotti/$id/match',
              params: { id: propertyId },
              search: {} as never,
            })
          }
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 'var(--r-sm)',
            background: 'var(--accent, #f97316)',
            color: '#fff',
            fontWeight: 'var(--fw-semibold)',
            fontSize: 'var(--fs-12)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <span aria-hidden>📋</span>
          Apri partecipazione
        </button>
        <button
          type="button"
          title="Altre azioni"
          style={{
            padding: '10px 14px',
            borderRadius: 'var(--r-sm)',
            background: 'oklch(28% 0.01 240)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 'var(--fw-semibold)',
            fontSize: 'var(--fs-13)',
          }}
          onClick={() => window.alert('Azioni — TODO menu')}
        >
          ⋯
        </button>
      </div>
    </aside>
  )
}

function Digit({ n, u }: { n: number; u: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '4px 0' }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 'var(--fw-semibold)',
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          color: '#fff',
          lineHeight: 1.1,
        }}
      >
        {String(n).padStart(2, '0')}
      </div>
      <div
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'oklch(65% 0.01 240)',
          marginTop: 2,
        }}
      >
        {u}
      </div>
    </div>
  )
}

function NumeriChiaveCard({ p }: { p: PropertyRow }) {
  const ribassoPct = p.vendita?.ribasso_cumulato_pct
  const eurMq = p.vendita?.eur_mq
  const tentativo = p.vendita?.tentativo_corrente
  const tentativiTot = p.vendita?.tentativi_totali

  const superficie = p.superficie_mq ?? p.superficie_commerciale_mq

  return (
    <aside className="section" aria-label="Info chiave">
      <div className="section-head">
        <div className="title">Info Chiave</div>
      </div>
      <div
        className="section-body"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--sp-2)',
          padding: 'var(--sp-3)',
          textAlign: 'center',
        }}
      >
        <Stat value={fmtMoneyShort(p.prezzo_base)} label="Base" />
        <Stat value={fmtMoneyShort(p.cauzione)} label="Cauzione" />
        <Stat
          value={
            ribassoPct != null
              ? `${ribassoPct > 0 ? '+' : ''}${ribassoPct.toFixed(0)}%`
              : '—'
          }
          label="Vs perizia"
          accent={ribassoPct != null && ribassoPct < 0}
        />
        <Stat
          value={superficie != null ? `${superficie}m²` : '—'}
          label="Superficie"
        />
        <Stat
          value={eurMq != null ? `€${Math.round(eurMq).toLocaleString('it-IT')}` : '—'}
          label="€/m²"
        />
        <Stat
          value={
            tentativo != null
              ? `${tentativo}/${tentativiTot ?? '?'}`
              : '—'
          }
          label="Tentativo"
        />
      </div>
      {(p.occupazione || p.problematiche) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
            padding: '0 var(--sp-3) var(--sp-3)',
            borderTop: '1px solid var(--line)',
            paddingTop: 'var(--sp-3)',
          }}
        >
          {p.occupazione && p.occupazione !== 'sconosciuto' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span
                className="muted"
                style={{
                  fontSize: 'var(--fs-10)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  minWidth: 78,
                }}
              >
                Occupazione
              </span>
              <span
                className={`chip ${p.occupazione === 'libero' ? 'ok' : 'outline'}`}
              >
                {p.occupazione.replace(/_/g, ' ')}
              </span>
            </div>
          )}
          {p.problematiche && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                className="muted"
                style={{
                  fontSize: 'var(--fs-10)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                }}
              >
                Problematiche
              </span>
              <span
                style={{ fontSize: 'var(--fs-12)', whiteSpace: 'pre-wrap' }}
              >
                {p.problematiche}
              </span>
            </div>
          )}
        </div>
      )}
    </aside>
  )
}

function fmtMoneyShort(v: number | null | undefined): string {
  if (v == null) return '—'
  if (Math.abs(v) >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`
  if (Math.abs(v) >= 1000) return `€${(v / 1000).toFixed(0)}k`
  return `€${v}`
}

function Stat({
  value,
  label,
  accent,
}: {
  value: string
  label: string
  accent?: boolean
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-15)',
          fontWeight: 'var(--fw-semibold)',
          fontVariantNumeric: 'tabular-nums',
          color: accent ? 'var(--ok-ink)' : 'var(--ink-1)',
        }}
      >
        {value}
      </div>
      <div
        className="muted"
        style={{
          fontSize: 'var(--fs-10)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
        }}
      >
        {label}
      </div>
    </div>
  )
}

function ConsulenteCard({ name }: { name?: string | null }) {
  if (!name) return null
  const ini = name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return (
    <aside className="section" aria-label="Consulente assegnato">
      <div className="section-head">
        <div className="title">Consulente assegnato</div>
      </div>
      <div className="section-body" style={{ padding: 'var(--sp-3)' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}
        >
          <div
            className="avatar lg"
            style={{ background: 'oklch(58% 0.12 170)' }}
          >
            {ini}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 'var(--fs-13)',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              {name}
            </div>
            <div
              className="muted"
              style={{
                fontSize: 'var(--fs-10)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
              }}
            >
              Senior consultant
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}

function CronologiaMiniCard({
  items,
  isLoading,
}: {
  items: { id: string; kind: string; title: string; body?: string | null; occurred_at: string }[]
  isLoading: boolean
}) {
  const recent = items.slice(0, 5)
  return (
    <aside className="section" aria-label="Cronologia recente">
      <div className="section-head">
        <div className="title">Cronologia</div>
      </div>
      <div
        className="section-body"
        style={{
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
        }}
      >
        {isLoading && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Caricamento…
          </div>
        )}
        {!isLoading && recent.length === 0 && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun evento ancora.
          </div>
        )}
        {recent.map((e) => (
          <div
            key={e.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingBottom: 'var(--sp-2)',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--sp-2)',
                fontSize: 'var(--fs-12)',
                fontWeight: 'var(--fw-medium)',
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {e.title}
              </span>
              <span
                className="muted"
                style={{
                  fontSize: 'var(--fs-10)',
                  fontFamily: 'var(--font-mono)',
                  whiteSpace: 'nowrap',
                }}
              >
                {relativeTime(e.occurred_at)}
              </span>
            </div>
            {e.body && (
              <div
                className="muted"
                style={{
                  fontSize: 'var(--fs-11)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {e.body}
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  )
}
